import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Brain, Upload, Link as LinkIcon, Loader2, Sparkles, History, CreditCard, TrendingUp, Users, BarChart3, Zap, Target, Globe, Menu, Moon, Sun, User, LogOut, Video, Lightbulb, Download, FileText, Home as HomeIcon, ChevronDown, X, ChevronUp, AlertCircle, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import LiveCamera from '@/components/LiveCamera';
import ShareButton from '@/components/ShareButton';
import ExpandedAnalytics from '@/components/ExpandedAnalytics';
import CompareImages from '@/components/CompareImages';
import DocumentAnalysis from '@/components/DocumentAnalysis';
import ComprehensiveAnalysisResults from '@/components/ComprehensiveAnalysisResults';
import ScrollToTop from '@/components/ScrollToTop';
import BestOfThreeResults from '@/components/BestOfThreeResults';
import RatingFeedback from '@/components/RatingFeedback';
import Footer from '@/components/Footer';
import AIEnhancement from '@/components/AIEnhancement';
import AuthModal from '@/components/AuthModal';
import CreditBadge from '@/components/CreditBadge';
import InsufficientCreditsModal from '@/components/InsufficientCreditsModal';
import AuthPromptOverlay from '@/components/AuthPromptOverlay';
import AddToHomeScreen from '@/components/AddToHomeScreen';
import FormattedContentAnalysis from '@/components/FormattedContentAnalysis';
import AnalysisNotificationPrompt from '@/components/AnalysisNotificationPrompt';
import DemoAnalysisShowcase from '@/components/DemoAnalysisShowcase';
import PersonaDemoModal from '@/components/PersonaDemoModal';
import { generateSingleAnalysisWord } from '@/utils/wordGenerator';
import { generatePDFFromHTML } from '@/utils/pdfGenerator';
import { celebratePaymentSuccess, checkAndWarnLowCredits } from '@/utils/creditHelpers';
import { compressVideo, shouldCompress, estimateCompressedSize } from '@/utils/videoCompressor';
import { personaData } from '@/constants/personaData';
import demoPersonaData from '@/data/DEMO_PERSONA_DATA.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mapping persona roles to demo data keys
const PERSONA_KEY_MAP = {
  'Content Creator': 'content_creator',
  'Marketer': 'marketer',
  'Social Media Manager': 'social_media_manager',
  'Copywriter': 'copywriter',
  'Designer': 'designer',
  'Sales Team': 'sales_team',
  'Founder': 'founder',
  'Educator': 'educator'
};

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#a855f7'];

// Typing animation utility
const typeText = async (setText, fullText, speed = 30) => {
  for (let i = 0; i <= fullText.length; i++) {
    setText(fullText.substring(0, i));
    await new Promise(resolve => setTimeout(resolve, speed));
  }
};

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, logout, isAuthenticated, updateCredits, deductCreditsOptimistic, authToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Refs for file inputs to properly reset them
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [userId, setUserId] = useState(null);
  const [audienceOptions, setAudienceOptions] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  
  // Auth and credit modals
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [requiredCredits, setRequiredCredits] = useState(2);
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);
  const [isDemoAnalysis, setIsDemoAnalysis] = useState(false); // Track if it's a demo
  const [demoPersonaKey, setDemoPersonaKey] = useState(null); // Track which persona for demo data
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [isDemoExpanded, setIsDemoExpanded] = useState(false); // Demo section collapsed by default
  
  const [inputType, setInputType] = useState('image');
  const [bestOfThreeImages, setBestOfThreeImages] = useState([]);
  const bestOfThreeInputRef = useRef(null);
  const [textInput, setTextInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCredits, setVideoCredits] = useState(15);
  const [videoCompressing, setVideoCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false); // Dynamic credits based on duration
  
  const [region, setRegion] = useState('World');
  const [country, setCountry] = useState('World'); // Changed from 'US' to 'World' as default
  const [gender, setGender] = useState('All');
  const [ageRange, setAgeRange] = useState([13, 85]);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [customQuestion, setCustomQuestion] = useState('');
  
  // Progress and fun facts states
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [currentFunFact, setCurrentFunFact] = useState('');
  const [shownFacts, setShownFacts] = useState([]);
  
  // Persona sheet and guided tour states
  const [openSheet, setOpenSheet] = useState(false);
  const [highlightAnalysis, setHighlightAnalysis] = useState(false);
  const [showScrollPrompt, setShowScrollPrompt] = useState(false);
  const [pulseButton, setPulseButton] = useState(false);
  const [activeTyping, setActiveTyping] = useState(null); // 'content' or 'question'
  const [audienceFiltersExpanded, setAudienceFiltersExpanded] = useState(false); // Collapsed by default
  
  // Demo modal states
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [selectedDemoPersona, setSelectedDemoPersona] = useState(null);
  const [liveCameraKey, setLiveCameraKey] = useState(0); // Key to force LiveCamera remount
  
  const funFacts = [
    "Did you know? Humans can recognize over 10,000 facial expressions! 😊",
    "Fun fact: Your brain processes emotional responses in just 0.2 seconds ⚡",
    "Research shows that smiling can actually make you feel happier! 😄",
    "Emotions are contagious - seeing someone smile activates your smile muscles! 🤗",
    "The color red can increase heart rate and evoke strong emotions ❤️",
    "Music can trigger the same pleasure chemicals as chocolate! 🎵",
    "Babies can recognize their mother's emotional state through facial expressions 👶",
    "Laughter releases endorphins - nature's feel-good chemicals! 😂",
    "Your pupils dilate when you see something emotionally exciting! 👀",
    "Emotional memories are stored more strongly than neutral ones 🧠",
    "Emotional intelligence can be more important than IQ for success 🎯",
    "Your heart rate synchronizes with music tempo! 🎶",
    "Kindness releases oxytocin - the bonding hormone ❤️",
    "Dogs can sense human emotions through smell 🐕",
    "The color blue can have a calming effect on emotions 💙",
    "Social connections are as important as diet and exercise for longevity 👥",
    "Positive emotions broaden your thinking and creativity 🌈",
    "Your body language affects your emotions - power posing works! 💪",
    "Gratitude journaling increases happiness by 25% 📔",
    "Fear and excitement produce similar physical responses ⚡"
  ];

  // Reset custom question and clear results when input type changes
  useEffect(() => {
    setCustomQuestion('');
    // Clear any existing results when switching analysis types
    setResult(null);
    setIsDemoAnalysis(false);
  }, [inputType]);
  
  // CRITICAL FIX: Listen for page visibility changes (user switching tabs/coming back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && result && !loading) {
        console.log('👁️ Page became visible with old results - triggering COMPLETE refresh');
        goHome(); // Use complete refresh function
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [result, loading]);
  
  // CRITICAL FIX: Listen for browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      if (window.location.pathname === '/' || window.location.pathname === '') {
        console.log('🔙 Browser back/forward to home - triggering COMPLETE refresh');
        goHome();
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('header')) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    const saved = localStorage.getItem('darkMode');
    const storedDarkMode = saved !== null ? saved === 'true' : true; // Default to true (dark mode)
    setDarkMode(storedDarkMode);
    if (storedDarkMode) {
      document.body.classList.add('dark');
    }
    initUser();
    loadAudienceOptions();
    
    // Check if this is a forced refresh (from goHome or nav buttons)
    const urlParams = new URLSearchParams(window.location.search);
    const isClear = urlParams.has('clear');
    const isRefresh = urlParams.has('refresh') || urlParams.has('t');
    const isForced = isClear || isRefresh;
    
    if (isForced) {
      console.log('🔥 FORCED CLEAR DETECTED - Nuclear state reset', { isClear, isRefresh });
      // Remove the query parameter from URL to clean it up
      window.history.replaceState({}, document.title, '/');
      
      // EXTRA: Clear any potential localStorage caching
      try {
        // Don't clear auth tokens, just analysis data if any exists
        const keysToRemove = Object.keys(localStorage).filter(key => 
          key.includes('result') || key.includes('analysis') || key.includes('lastAnalysis')
        );
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('🗑️ Cleared localStorage keys:', keysToRemove);
      } catch (e) {
        console.log('Could not clear localStorage:', e);
      }
    }
    
    // CRITICAL FIX: Force clear any persisted results when component mounts
    // This ensures fresh start when user navigates back to home page
    console.log('🏠 Home page mounted - COMPLETE REFRESH of all state', { isForced });
    
    // Clear all results and analysis state
    setResult(null);
    setIsDemoAnalysis(false);
    setLoading(false);
    
    // Clear all input fields
    setTextInput('');
    setCustomQuestion('');
    setUrlInput('');
    setImageFile(null);
    setImagePreview(null);
    setVideoFile(null);
    setVideoPreview(null);
    setVideoDuration(0);
    setBestOfThreeImages([]);
    
    // Reset input type to default (Image tab)
    setInputType('image');
    
    // Force remount of LiveCamera
    setLiveCameraKey(prev => prev + 1);
    
    // Reset all file input DOM elements
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (bestOfThreeInputRef.current) bestOfThreeInputRef.current.value = '';
    
    console.log('✅ Home page state completely refreshed - ready for new analysis');
  }, []);
  
  // Update userId when user changes (login/logout)
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('✅ User authenticated, updating userId:', user.id);
      setUserId(user.id);
    }
  }, [isAuthenticated, user]);

  // Handle payment success and check low credits
  useEffect(() => {
    // Check for payment success in URL
    const paymentSuccess = searchParams.get('payment_success');
    const creditsAdded = searchParams.get('credits_added');
    
    if (paymentSuccess === 'true' && creditsAdded) {
      // Update credits
      if (updateCredits) {
        updateCredits();
      }
      
      // Celebrate!
      setTimeout(() => {
        celebratePaymentSuccess(parseInt(creditsAdded));
      }, 500);
      
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Check for low credits warning
    if (user && user.credits !== undefined) {
      checkAndWarnLowCredits(user.credits);
    }
  }, [searchParams, user, updateCredits]);

  // DEBUG: Log user state whenever it changes
  useEffect(() => {
    console.log('👤 User state updated:', {
      isAuthenticated,
      authToken: authToken ? 'exists (length: ' + authToken.length + ')' : 'null/undefined',
      user: user ? {
        email: user.email,
        credits: user.credits,
        hasCreditsField: 'credits' in user,
        creditsType: typeof user.credits
      } : null
    });
  }, [user, isAuthenticated, authToken]);


  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    if (newMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  };

  // Navigate to home and reset state - COMPLETE REFRESH WITH HARD RELOAD
  const goHome = () => {
    console.log('🚀 FINAL FIX: goHome - using location.replace for complete reset');
    
    // NUCLEAR OPTION: Use location.replace to force complete reload
    // This is different from location.href - it replaces current history entry
    // Ensures NO state can persist, browser treats it as completely new page
    window.location.replace('/?clear=' + Date.now());
  };

  // Helper function to clear all inputs
  const clearInputs = () => {
    setTextInput('');
    setCustomQuestion('');
    setUrlInput('');
    setImageFile(null);
    setImagePreview(null);
    setVideoFile(null);
    setVideoPreview(null);
    setVideoDuration(0);
    setResult(null);
    setIsDemoAnalysis(false);
    setBestOfThreeImages([]);
    // Force remount of LiveCamera component by changing key
    setLiveCameraKey(prev => prev + 1);
    
    // CRITICAL: Reset file input DOM elements to clear browser's file selection memory
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
    if (bestOfThreeInputRef.current) {
      bestOfThreeInputRef.current.value = '';
    }
  };

  // Guided sample analysis flow with typing animation and auto-scroll
  const handleTrySampleAnalysis = async (sampleContent, sampleQuestion, personaRole) => {
    // Mark as demo analysis - no auth required
    const personaKey = PERSONA_KEY_MAP[personaRole];
    
    // Step 1: Close the sheet
    setOpenSheet(false);
    
    // Step 2: Switch to text input and clear first
    setInputType('text');
    setTextInput('');
    setCustomQuestion('');
    
    // Step 3: Show a toast message
    toast.success('Watch the magic happen... ✨');
    
    // Step 4: Wait a moment, then scroll to first textarea
    setTimeout(async () => {
      // Scroll to content input area smoothly
      const contentTextarea = document.querySelector('textarea[placeholder*="Revolutionary"]');
      if (contentTextarea) {
        contentTextarea.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Highlight the section and mark content as active
        setHighlightAnalysis(true);
        setActiveTyping('content');
        
        // Wait a bit, then start typing in content box
        await new Promise(resolve => setTimeout(resolve, 500));
        await typeText(setTextInput, sampleContent, 20);
        
        // Step 5: After content is typed, scroll to custom question
        setActiveTyping(null);
        await new Promise(resolve => setTimeout(resolve, 800));
        
        const questionTextarea = document.querySelector('textarea[placeholder*="How would this perform"]');
        if (questionTextarea) {
          // Scroll to show the custom question box
          questionTextarea.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Mark question as active
          setActiveTyping('question');
          
          // Wait, then type the question
          await new Promise(resolve => setTimeout(resolve, 500));
          await typeText(setCustomQuestion, sampleQuestion || '', 20);
          
          // Step 6: After question is typed, automatically trigger analysis
          setActiveTyping(null);
          await new Promise(resolve => setTimeout(resolve, 1000));
          setHighlightAnalysis(false);
          
          // IMPORTANT: Set the demo states FIRST, then trigger analysis
          setIsDemoAnalysis(true);
          setDemoPersonaKey(personaKey);
          
          // Auto-trigger the analysis with demo data  
          toast.info('Starting analysis... 🔍', { duration: 3000 });
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Call analyze with demo mode explicit flag
          handleAnalyze(true, personaKey);
        }
      }
    }, 400);
  };

  const initUser = async () => {
    // If user is authenticated via Auth context, use that
    if (isAuthenticated && user) {
      setUserId(user.id);
      return;
    }
    
    // Otherwise, fall back to IP-based tracking
    let storedUserId = localStorage.getItem('userId');
    if (!storedUserId) {
      const response = await axios.post(`${API}/users`);
      storedUserId = response.data.id;
      localStorage.setItem('userId', storedUserId);
    }
    setUserId(storedUserId);
  };

  const loadAudienceOptions = async () => {
    try {
      const response = await axios.get(`${API}/audience-options`);
      setAudienceOptions(response.data);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const getAudienceFilters = () => ({
    region,
    custom: {
      country,
      gender,
      age_range: `${ageRange[0]}-${ageRange[1]}`,
      professions: selectedProfessions
    }
  });

  const handleDownloadReport = async () => {
    console.log('Download Word button clicked!');
    if (!result) {
      console.error('No result to download');
      toast.error('No analysis to download');
      return;
    }
    
    console.log('Starting Word generation...', result);
    try {
      const success = await generateSingleAnalysisWord(result);
      console.log('Word generation result:', success);
      toast.success('Word report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to generate report: ${error.message}`);
    }
  };

  const handleDownloadPDF = async () => {
    console.log('Download PDF button clicked!');
    if (!result) {
      console.error('No result to download');
      toast.error('No analysis to download');
      return;
    }
    
    console.log('Starting PDF generation...');
    try {
      // Check if element exists
      const element = document.getElementById('analysis-results-pdf');
      if (!element) {
        throw new Error('Analysis results not found. Please wait for the page to fully load.');
      }
      
      toast.loading('Generating PDF...', { id: 'pdf-gen' });
      
      // Small delay to ensure all content is rendered
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await generatePDFFromHTML('analysis-results-pdf', `emoQ-analysis-${result.content_type}`);
      toast.success('PDF report downloaded successfully!', { id: 'pdf-gen' });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`, { id: 'pdf-gen' });
    }
  };

  const getRandomFunFact = () => {
    const today = new Date().toDateString();
    const sessionKey = `shownFacts_${today}`;
    let todayShown = JSON.parse(localStorage.getItem(sessionKey) || '[]');
    
    const availableFacts = funFacts.filter((_, idx) => !todayShown.includes(idx));
    if (availableFacts.length === 0) {
      localStorage.removeItem(sessionKey);
      todayShown = [];
    }
    
    const randomIdx = Math.floor(Math.random() * (availableFacts.length || funFacts.length));
    const factIdx = availableFacts.length ? funFacts.indexOf(availableFacts[randomIdx]) : randomIdx;
    
    todayShown.push(factIdx);
    localStorage.setItem(sessionKey, JSON.stringify(todayShown));
    
    return funFacts[factIdx];
  };

  const handleAnalyze = async (forceIsDemo = null, forceDemoKey = null) => {
    // CRITICAL: Clear previous results IMMEDIATELY before any checks
    // This prevents old results from showing when user starts a new analysis
    setResult(null);
    setIsDemoAnalysis(false);
    
    // Use provided parameters or fall back to state
    const isDemoMode = forceIsDemo !== null ? forceIsDemo : isDemoAnalysis;
    const demoKey = forceDemoKey !== null ? forceDemoKey : demoPersonaKey;
    
    console.log('🔍 handleAnalyze called - isDemoMode:', isDemoMode, 'demoKey:', demoKey);
    
    // Start loading after clearing results
    setLoading(true);
    
    // Check if user is authenticated (skip for demo analyses)
    if (!isAuthenticated && !isDemoMode) {
      setLoading(false);
      setShowAuthModal(true);
      return;
    }
    
    // Check input validation (skip for demo analyses)
    if (!isDemoMode) {
      if (inputType === 'text' && !textInput.trim()) {
        setLoading(false);
        toast.error('Please enter some text');
        return;
      }
      if (inputType === 'url' && !urlInput.trim()) {
        setLoading(false);
        toast.error('Please enter a URL');
        return;
      }
      if (inputType === 'image' && !imageFile) {
        setLoading(false);
        toast.error('Please upload an image');
        return;
      }
      if (inputType === 'video' && !videoFile) {
        setLoading(false);
        toast.error('Please upload a video');
        return;
      }
      if (inputType === 'video' && videoDuration > 90) {
        setLoading(false);
        toast.error('Video must be 90 seconds or less for fast processing');
        return;
      }
      if (inputType === 'best-of-3' && bestOfThreeImages.length < 2) {
        setLoading(false);
        toast.error('Please upload at least 2 images to compare');
        return;
      }
    }
    
    // Check credits (skip for demo analyses)
    if (!isDemoMode) {
      const creditsNeeded = inputType === 'text' ? 2 : 
                           inputType === 'compare' ? 10 : 
                           inputType === 'document' ? 20 : 
                           inputType === 'video' ? 15 :
                           inputType === 'best-of-3' ? 15 : 5;
      
      // Convert credits to number to handle string values
      const userCredits = user?.credits ? Number(user.credits) : 0;
      
      console.log('💳 Credit check:', { 
        userCreditsRaw: user?.credits,
        userCreditsType: typeof user?.credits,
        userCreditsNumber: userCredits,
        creditsNeeded, 
        hasEnough: userCredits >= creditsNeeded,
        user: user ? { email: user.email, credits: user.credits, creditsUndefined: user.credits === undefined } : null
      });
      
      // Check if credits field exists and has a value
      if (!user || user.credits === undefined || user.credits === null) {
        console.log('⚠️ User credits not loaded, waiting for user state...');
        setLoading(false);
        toast.error('Loading user data, please try again in a moment');
        return;
      }
      
      if (userCredits < creditsNeeded) {
        console.log('❌ Insufficient credits, showing modal');
        setLoading(false);
        setRequiredCredits(creditsNeeded);
        setShowInsufficientCredits(true);
        return;
      }
      console.log('✅ Credits check passed');
    }

    // Loading already set at function start, no need to set again
    setAnalysisProgress(0);
    setCurrentFunFact(getRandomFunFact());
    setShowNotificationPrompt(true); // Show notification prompt for video analysis
    
    // Reset compression progress when starting analysis
    if (compressionProgress === 100) {
      setCompressionProgress(0);
    }
    
    // Simulate progress - adjusted for comprehensive analysis (25-35 sec for text, 35-50 sec for images)
    let elapsedTime = 0;
    const estimatedTime = inputType === 'image' ? 45 : 30;  // Images take longer (2 LLM calls)
    const progressInterval = setInterval(() => {
      elapsedTime += 2;
      setAnalysisProgress(prev => {
        // Calculate progress based on estimated time
        const estimatedProgress = Math.min((elapsedTime / estimatedTime) * 100, 95);
        // Add some randomness but never exceed 95% until complete
        const newProgress = Math.min(estimatedProgress + Math.random() * 3, 95);
        return Math.max(prev, newProgress); // Always move forward
      });
      if (Math.random() > 0.7) {
        setCurrentFunFact(getRandomFunFact());
      }
    }, 2000);
    
    try {
      let response;
      
      // Check if this is a demo analysis with pre-generated data
      if (isDemoMode && demoKey && demoPersonaData[demoKey]) {
        console.log('🎭 Using pre-generated demo data for:', demoKey);
        
        // Simulate realistic loading time (15-20 seconds)
        await new Promise(resolve => setTimeout(resolve, 15000 + Math.random() * 5000));
        
        // Get pre-generated data
        const demoData = demoPersonaData[demoKey];
        
        // Format the demo analysis data to match API response structure
        response = {
          data: {
            ...demoData.analysis,
            user_id: userId || 'demo-user',
            user_ip: 'demo-ip',
            is_demo_analysis: true,  // IMPORTANT: Flag this as demo
            demo_persona_key: demoKey  // Store which persona this is
          }
        };
        
        // Update the states so AIEnhancement knows this is a demo
        setIsDemoAnalysis(true);
        setDemoPersonaKey(demoKey);
        
        console.log('✅ Demo data loaded successfully');
      } else if (inputType === 'text') {
        response = await axios.post(`${API}/analyze/text`, {
          text: textInput,
          user_id: userId,
          audience_filters: getAudienceFilters(),
          custom_question: customQuestion || null
        }, { 
          timeout: 180000,  // 3 minutes for text analysis
          withCredentials: true 
        });
      } else if (inputType === 'image') {
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('user_id', userId);
        formData.append('audience_filters', JSON.stringify(getAudienceFilters()));
        if (customQuestion) formData.append('custom_question', customQuestion);
        response = await axios.post(`${API}/analyze/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,  // 3 minutes for image analysis (increased to match text/URL)
          withCredentials: true
        });
      } else if (inputType === 'video') {
        const formData = new FormData();
        formData.append('file', videoFile);
        formData.append('user_id', userId);
        formData.append('audience_filters', JSON.stringify(getAudienceFilters()));
        if (customQuestion) formData.append('custom_question', customQuestion);
        
        // Show file size in toast
        const fileSizeMB = (videoFile.size / (1024 * 1024)).toFixed(1);
        toast.info(`Uploading video (${fileSizeMB}MB)... This may take 2-3 minutes`, { duration: 7000 });
        
        // Retry logic for video uploads (more prone to network issues)
        let retryCount = 0;
        const maxRetries = 2;
        
        while (retryCount <= maxRetries) {
          try {
            response = await axios.post(`${API}/analyze/video`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 300000,  // 5 minutes for video processing (increased from 4)
              withCredentials: true,
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  if (percentCompleted < 100) {
                    console.log(`Upload progress: ${percentCompleted}%`);
                  } else {
                    console.log('Upload complete, processing video...');
                    toast.info('Video uploaded! Processing frames and audio...', { duration: 5000 });
                  }
                }
              }
            });
            break; // Success, exit retry loop
          } catch (uploadError) {
            retryCount++;
            if (retryCount <= maxRetries) {
              console.log(`Video upload attempt ${retryCount} failed, retrying...`);
              toast.warning(`Network issue detected. Retrying... (Attempt ${retryCount + 1}/${maxRetries + 1})`, { duration: 3000 });
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
            } else {
              throw uploadError; // Max retries exceeded, throw error
            }
          }
        }
      } else if (inputType === 'best-of-3') {
        // Use NEW single-call comparative endpoint for true comparison
        toast.info('Analyzing all images together for comparison...', { duration: 3000 });
        
        const formData = new FormData();
        formData.append('file1', bestOfThreeImages[0].file);
        formData.append('file2', bestOfThreeImages[1].file);
        if (bestOfThreeImages.length === 3) {
          formData.append('file3', bestOfThreeImages[2].file);
        }
        formData.append('user_id', userId);
        formData.append('audience_filters', JSON.stringify(getAudienceFilters()));
        formData.append('custom_question', customQuestion || 'Determine which image is most emotionally effective');
        
        response = await axios.post(`${API}/analyze/best-of-three`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,  // 5 minutes for multi-image comparative analysis
          withCredentials: true
        });
      } else {
        response = await axios.post(`${API}/analyze/url`, {
          url: urlInput,
          user_id: userId,
          audience_filters: getAudienceFilters(),
          custom_question: customQuestion || null
        }, { 
          timeout: 180000,  // 3 minutes for URL analysis
          withCredentials: true 
        });
      }
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      setResult(response.data);
      setLoading(false);
      toast.success('Analysis complete!');
      
      // Reset notification prompt so it shows again on next analysis (if not yet subscribed)
      // Only reset after a delay to allow user to see success
      setTimeout(() => {
        setShowNotificationPrompt(false);
      }, 3000);
      
      // Immediately update credits in UI (optimistic update)
      if (isAuthenticated && user && !isDemoMode && deductCreditsOptimistic) {
        const creditCost = inputType === 'text' ? 2 : 
                          inputType === 'compare' ? 10 : 
                          inputType === 'best-of-3' ? 15 :  // Compare feature
                          inputType === 'document' ? 20 : 
                          inputType === 'video' ? 15 : 5;
        
        // Instant UI update - user sees credit deduction immediately
        deductCreditsOptimistic(creditCost);
      }
      
      // Then fetch actual credits from backend (for accuracy)
      if (isAuthenticated && updateCredits) {
        updateCredits();
      }
      
      // Clear input fields after successful analysis
      setTextInput('');
      setUrlInput('');
      setImageFile(null);
      setImagePreview(null);
      setCustomQuestion('');
      
      // Show scroll prompt for 5 seconds
      setShowScrollPrompt(true);
      setTimeout(() => setShowScrollPrompt(false), 5000);
      
      if (window.innerWidth < 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      // ALWAYS log error details
      console.log('=== ANALYSIS ERROR DEBUG ===');
      console.log('Error message:', error.message);
      console.log('Error code:', error.code);
      console.log('Response status:', error.response?.status);
      console.log('Response data:', error.response?.data);
      console.log('Full error:', error);
      console.log('=== END DEBUG ===');
      
      setLoading(false);
      setResult(null);
      clearInterval(progressInterval);
      
      // Reset notification prompt so it can show again on next analysis
      setShowNotificationPrompt(false);
      
      // More specific error messages with actionable guidance
      let errorMessage = 'Analysis failed. Please try again.';
      let showMyAnalysesLink = false;
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        if (inputType === 'video') {
          errorMessage = 'Video analysis timeout. Try with: 1) Shorter video (<30s), 2) Smaller file size, or 3) Better internet connection';
        } else {
          errorMessage = 'Analysis timeout - taking too long. Please try with shorter content.';
        }
      } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        if (inputType === 'video') {
          errorMessage = 'Network error during video upload. Your video may be too large or network is slow. Please try: 1) Compress video to under 50MB at https://www.videosmaller.com, 2) Use shorter clips (30-60s), 3) Check your internet speed, 4) Try again with stable WiFi.';
        } else {
          errorMessage = 'Network error occurred. Your analysis may have completed - check "My Analyses" to see if it saved.';
          showMyAnalysesLink = true;
        }
      } else if (error.response?.status === 500) {
        errorMessage = `Server error: ${error.response?.data?.detail || 'Internal processing error'}`;
      } else if (error.response?.status === 413) {
        errorMessage = 'Content is too large. Please try with smaller content.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      // Show error with action button for network errors
      if (showMyAnalysesLink) {
        toast.error(errorMessage, {
          duration: 8000,
          action: {
            label: 'Check My Analyses',
            onClick: () => navigate('/my-analyses')
          }
        });
      } else {
        toast.error(errorMessage, {
          duration: 6000,
          action: {
            label: 'Retry',
            onClick: () => handleAnalyze(forceIsDemo, forceDemoKey)
          }
        });
      }
    } finally {
      setLoading(false);
      setIsDemoAnalysis(false); // Reset demo flag after analysis
      
      // Clear inputs after demo analysis completes
      if (isDemoAnalysis) {
        setTimeout(() => {
          setTextInput('');
          setCustomQuestion('');
          setUrlInput('');
          setImageFile(null);
          setImagePreview(null);
        }, 1000);
      }
      
      setTimeout(() => {
        setAnalysisProgress(0);
        setCurrentFunFact('');
      }, 1000);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // CRITICAL: Clear old results immediately when new image is selected
      setResult(null);
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const emotionChartData = result ? Object.entries(result.emotions)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 8)
    .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value: Math.round(value) }))
    : [];

  const demographicData = result ? Object.entries(result.audience_reactions).map(([name, reaction], idx) => ({
    name: name.replace(' audience', '').replace(/\([^)]*\)/g, '').trim(),
    fullName: name,
    reaction: reaction,
    color: COLORS[idx % COLORS.length]
  })) : [];

  const padData = result ? [
    { metric: 'Pleasure', value: Math.round(result.valence * 100) },
    { metric: 'Arousal', value: Math.round(result.arousal_level * 100) },
    { metric: 'Dominance', value: Math.round(result.dominance * 100) }
  ] : [];

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-b from-slate-50 to-blue-50'}`}>
      <header className={`border-b ${darkMode ? 'border-gray-700 bg-gray-800/80' : 'border-blue-100 bg-white/80'} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-6xl mx-auto px-2 sm:px-4 py-1.5 md:py-2 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={goHome}>
            <img 
              src={darkMode 
                ? "https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/p4o00bk8_Emo.zip%20-%205.png"
                : "https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/3r5buf5n_Emo.zip%20-%204.png"
              }
              alt="EmoQ - Go to Home"
              title="Go to Home"
              className="h-10 sm:h-12 md:h-14 w-auto transition-all"
            />
          </div>
          
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={toggleDarkMode} className="gap-2 transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-gray-400/30">
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <AddToHomeScreen darkMode={darkMode} />
            <Button variant="ghost" size="sm" onClick={goHome} className="gap-2 transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-gray-400/30">
              <HomeIcon className="w-4 h-4" />
              Home
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/history')} className="gap-2 transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-gray-400/30">
              <History className="w-4 h-4" />
              My Analyses
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/pricing')} className="gap-2 transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-gray-400/30">
              <CreditCard className="w-4 h-4" />
              Pricing
            </Button>
            
            {isAuthenticated && user && (
              <CreditBadge credits={user.credits || 0} darkMode={darkMode} />
            )}
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-gray-400/30">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={user?.picture} alt={user?.name || user?.email} />
                      <AvatarFallback>
                        {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline">{user?.name || user?.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div className="text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded transition-colors" onClick={() => navigate('/profile')}>
                      <div className="font-semibold">{user?.name || 'User'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">⚡ {user?.credits || 0} credits</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/pricing')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Top Up Credits
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowAuthModal(true)} className="gap-2 transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-blue-400/30 hover:border-blue-500">
                <User className="w-4 h-4" />
                Login
              </Button>
            )}
          </div>

          {/* Mobile right side with credit badge + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            {isAuthenticated && user && (
              <CreditBadge credits={user.credits || 0} darkMode={darkMode} />
            )}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <Menu className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-blue-100 bg-white'}`}>
            <div className="px-4 py-2 space-y-2">
              <Button variant="ghost" size="sm" onClick={toggleDarkMode} className="w-full justify-start gap-2">
                {darkMode ? <><Sun className="w-4 h-4" />Light Mode</> : <><Moon className="w-4 h-4" />Dark Mode</>}
              </Button>
              <AddToHomeScreen darkMode={darkMode} />
              <Button variant="ghost" size="sm" onClick={() => { goHome(); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <HomeIcon className="w-4 h-4" />
                Home
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <History className="w-4 h-4" />
                My Analyses
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <CreditCard className="w-4 h-4" />
                Pricing
              </Button>
              
              {isAuthenticated ? (
                <>
                  <div className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border-t dark:border-gray-700">
                    <div className="font-semibold">{user?.name || user?.email}</div>
                    <div className="text-xs text-gray-500">Credits: {user?.credits || 0}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                    <User className="w-4 h-4" />
                    My Profile
                  </Button>
                  <Button variant="ghost" size="sm" onClick={logout} className="w-full justify-start gap-2">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                  <User className="w-4 h-4" />
                  Login
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      {!result && (
        <div className="max-w-4xl mx-auto px-4 py-4 md:py-6 text-center">
          <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-3 ${darkMode ? 'text-white' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent'} leading-tight`} style={{fontFamily: 'Space Grotesk'}}>
            Measure the Emotional Quotient of Your Content
          </h1>
          <p className={`text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 md:mb-6 max-w-2xl mx-auto leading-relaxed`}>
            EmoQ.ai analyzes your images, videos, and text to predict how audiences will feel — before you post, publish, or present.
          </p>
        </div>
      )}

      {/* Who Is This For? Section */}
      {!result && (
        <div className={`py-4 md:py-6 ${darkMode ? 'bg-gray-800/50' : 'bg-gradient-to-b from-white to-blue-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-4 md:mb-6">
              <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>
                Who Is EmoQ.ai For?
              </h2>
              <p className={`text-sm md:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                See how EmoQ.ai helps your role
              </p>
            </div>
            
            {/* Demo Personas - Collapsible */}
            <div className={`p-4 rounded-xl border-2 ${darkMode ? 'bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-cyan-700' : 'bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-300'}`}>
              {/* Collapsible Header */}
              <button
                onClick={() => setIsDemoExpanded(!isDemoExpanded)}
                className={`w-full flex items-center justify-between py-3 px-4 rounded-lg transition-all ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-blue-100/50'}`}
              >
                <div className="text-left flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1" style={{fontFamily: 'Space Grotesk'}}>
                    <span className={`${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>Demo</span> <span className={`text-base font-normal ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>(click here)</span>
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                    Try instant analysis with pre-made personas
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-cyan-400' : 'text-blue-600'} font-semibold`}>
                    (No credits needed)
                  </p>
                </div>
                <ChevronDown 
                  className={`w-6 h-6 flex-shrink-0 ml-4 transition-transform ${isDemoExpanded ? 'rotate-180' : ''} ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}
                />
              </button>

              {/* Collapsible Content */}
              {isDemoExpanded && (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              {personaData.map((persona, idx) => (
                <Sheet key={idx} open={openSheet === persona.role} onOpenChange={(open) => setOpenSheet(open ? persona.role : false)}>
                  <SheetTrigger asChild>
                    <button className={`group p-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 cursor-pointer ${darkMode ? 'bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 border-2 border-gray-600 hover:border-cyan-500 shadow-lg hover:shadow-xl hover:shadow-cyan-500/50' : 'bg-gradient-to-br from-white to-blue-50 hover:from-blue-50 hover:to-blue-100 border-2 border-blue-200 hover:border-blue-400 shadow-lg hover:shadow-xl hover:shadow-blue-400/50'}`}>
                      <div className="flex items-start gap-2">
                        <div className="text-2xl flex-shrink-0 transition-transform group-hover:scale-110">{persona.icon}</div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-xs md:text-sm font-bold mb-0.5 ${darkMode ? 'text-white' : 'text-gray-900'} leading-tight group-hover:text-blue-600 transition-colors`}>
                            {persona.role}
                          </h3>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} leading-tight`}>
                            {persona.tagline}
                          </p>
                        </div>
                      </div>
                    </button>
                  </SheetTrigger>
                  <SheetContent 
                    side="right" 
                    className={`w-full sm:max-w-xl overflow-y-auto ${darkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white border-gray-200'}`}
                  >
                    <SheetHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-4xl">{persona.icon}</span>
                        <div>
                          <SheetTitle className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            For {persona.role}s
                          </SheetTitle>
                          <SheetDescription className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {persona.description}
                          </SheetDescription>
                        </div>
                      </div>
                    </SheetHeader>
                    
                    <div className="space-y-4 mt-6">
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-900'}`}>
                          How EmoQ.ai Helps You:
                        </h4>
                        <ul className="space-y-2">
                          {persona.examples.map((example, i) => (
                            <li key={i} className={`text-sm flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              <span className="text-green-500 mt-1">✓</span>
                              <span>{example}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                        <h4 className={`font-semibold mb-1 ${darkMode ? 'text-green-400' : 'text-green-900'}`}>
                          Results You'll See:
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {persona.benefits}
                        </p>
                      </div>
                      
                      {/* Sample Preview Section */}
                      <div className={`p-4 rounded-lg border ${darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                        <h4 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-purple-400' : 'text-purple-900'}`}>
                          <Lightbulb className="w-4 h-4" />
                          Try This Example:
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>Sample Content:</p>
                            <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>"{persona.sampleContent.substring(0, 100)}..."</p>
                          </div>
                          <div>
                            <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>Custom Question:</p>
                            <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>"{persona.sampleQuestion}"</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="space-y-3">
                        <Button 
                          onClick={() => {
                            const personaKey = PERSONA_KEY_MAP[persona.role];
                            setSelectedDemoPersona({ 
                              key: personaKey, 
                              data: demoPersonaData[personaKey],
                              role: persona.role,
                              icon: persona.icon
                            });
                            setShowDemoModal(true);
                            setOpenSheet(false);
                          }}
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 text-lg text-white shadow-lg"
                        >
                          <Sparkles className="w-5 h-5 mr-2 text-white" />
                          View Demo (Instant)
                        </Button>
                        
                        <Button 
                          onClick={() => handleTrySampleAnalysis(persona.sampleContent, persona.sampleQuestion, persona.role)}
                          variant="outline"
                          className={`w-full py-6 text-lg ${darkMode ? 'border-blue-400 text-blue-400 hover:bg-blue-900/20' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}`}
                        >
                          <Zap className="w-5 h-5 mr-2" />
                          Try Interactive Demo
                        </Button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Demo Analysis Showcase - Only for non-logged-in users */}
      {!isAuthenticated && !result && (
        <DemoAnalysisShowcase 
          darkMode={darkMode}
          onLoginClick={() => setShowAuthModal(true)}
        />
      )}

      {/* Main Analysis Section */}
      <div id="analysis-input"></div>
      <div className="max-w-5xl mx-auto px-4 pb-12">
        {(!result || inputType === 'live' || loading) ? (
          <div className="space-y-4 md:space-y-6">
            {/* Main Analysis Area - Highlighted with border and background */}
            <div className={`rounded-2xl border-4 p-6 md:p-8 transition-all duration-500 ${
              highlightAnalysis 
                ? 'ring-4 ring-yellow-400 ring-opacity-75 scale-[1.02]'
                : ''
            } ${
              darkMode 
                ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-blue-600' 
                : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border-blue-500'
            } shadow-2xl`}>
              
              {/* Analysis Area Title */}
              <div className="text-center mb-6 md:mb-8">
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Analyze Your Content
                </h2>
                <p className={`text-sm md:text-base ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Choose your content type and get instant emotional quotient insights
                </p>
              </div>

              {/* Input Type Buttons - Enhanced with unique colors per tab */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 justify-center mb-6">
                {/* Image Button - Blue */}
                <Button 
                  onClick={() => { clearInputs(); setInputType('image'); }} 
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className={`text-xs md:text-base px-3 py-2 md:px-4 md:py-2 relative transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 tap-highlight-transparent ${
                    inputType === 'image' 
                      ? '!bg-blue-600 !text-white !border-blue-600 shadow-xl shadow-blue-500/50 ring-2 ring-blue-400' 
                      : 'bg-transparent text-blue-700 dark:text-blue-300 border-2 border-blue-300 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 shadow-md hover:shadow-lg hover:shadow-blue-300/50'
                  }`}>
                  <Upload className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Image
                  <span className={`ml-1 text-[10px] md:text-xs px-1.5 py-0.5 rounded-full ${inputType === 'image' ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}>5⚡</span>
                </Button>
                
                {/* Text Button - Indigo */}
                <Button 
                  onClick={() => { clearInputs(); setInputType('text'); }} 
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className={`text-xs md:text-base px-3 py-2 md:px-4 md:py-2 relative transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 tap-highlight-transparent ${
                    inputType === 'text' 
                      ? '!bg-indigo-600 !text-white !border-indigo-600 shadow-xl shadow-indigo-500/50 ring-2 ring-indigo-400' 
                      : 'bg-transparent text-indigo-700 dark:text-indigo-300 border-2 border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 shadow-md hover:shadow-lg hover:shadow-indigo-300/50'
                  }`}>
                  <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Text
                  <span className={`ml-1 text-[10px] md:text-xs px-1.5 py-0.5 rounded-full ${inputType === 'text' ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'}`}>2⚡</span>
                </Button>
                
                {/* Web Link Button - Cyan */}
                <Button 
                  onClick={() => { clearInputs(); setInputType('url'); }} 
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className={`text-xs md:text-base px-3 py-2 md:px-4 md:py-2 relative transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 tap-highlight-transparent ${
                    inputType === 'url' 
                      ? '!bg-cyan-600 !text-white !border-cyan-600 shadow-xl shadow-cyan-500/50 ring-2 ring-cyan-400' 
                      : 'bg-transparent text-cyan-700 dark:text-cyan-300 border-2 border-cyan-300 hover:border-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 shadow-md hover:shadow-lg hover:shadow-cyan-300/50'
                  }`}>
                  <LinkIcon className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Web Link
                  <span className={`ml-1 text-[10px] md:text-xs px-1.5 py-0.5 rounded-full ${inputType === 'url' ? 'bg-white/20 text-white' : 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'}`}>5⚡</span>
                </Button>
                
                {/* Documents Button - Green */}
                <Button 
                  onClick={() => { clearInputs(); setInputType('document'); }} 
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className={`text-xs md:text-base px-3 py-2 md:px-4 md:py-2 relative transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 tap-highlight-transparent ${
                    inputType === 'document' 
                      ? '!bg-green-600 !text-white !border-green-600 shadow-xl shadow-green-500/50 ring-2 ring-green-400' 
                      : 'bg-transparent text-green-700 dark:text-green-300 border-2 border-green-300 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 shadow-md hover:shadow-lg hover:shadow-green-300/50'
                  }`}>
                  <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Documents
                  <span className={`ml-1 text-[10px] md:text-xs px-1.5 py-0.5 rounded-full ${inputType === 'document' ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>20⚡</span>
                </Button>
                
                {/* Video Button - Red */}
                <Button 
                  onClick={() => { clearInputs(); setInputType('video'); }} 
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className={`text-xs md:text-base px-3 py-2 md:px-4 md:py-2 relative transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 tap-highlight-transparent ${
                    inputType === 'video' 
                      ? '!bg-red-600 !text-white !border-red-600 shadow-xl shadow-red-500/50 ring-2 ring-red-400' 
                      : 'bg-transparent text-red-700 dark:text-red-300 border-2 border-red-300 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shadow-md hover:shadow-lg hover:shadow-red-300/50'
                  }`}>
                  <Video className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Video
                  <span className={`ml-1 text-[10px] md:text-xs px-1.5 py-0.5 rounded-full ${inputType === 'video' ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>15⚡</span>
                </Button>
                
                {/* Compare Button - Purple */}
                <Button 
                  onClick={() => { clearInputs(); setInputType('best-of-3'); }} 
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className={`text-xs md:text-base px-3 py-2 md:px-4 md:py-2 relative transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 tap-highlight-transparent ${
                    inputType === 'best-of-3' 
                      ? '!bg-purple-600 !text-white !border-purple-600 shadow-xl shadow-purple-500/50 ring-2 ring-purple-400' 
                      : 'bg-transparent text-purple-700 dark:text-purple-300 border-2 border-purple-300 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 shadow-md hover:shadow-lg hover:shadow-purple-300/50'
                  }`}>
                  <Trophy className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Compare
                  <span className={`ml-1 text-[10px] md:text-xs px-1.5 py-0.5 rounded-full ${inputType === 'best-of-3' ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'}`}>15⚡</span>
                </Button>
                
                {/* Live Button - Teal */}
                <Button 
                  onClick={() => { clearInputs(); setInputType('live'); }} 
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className={`text-xs md:text-base px-3 py-2 md:px-4 md:py-2 relative transition-all duration-200 transform hover:scale-105 hover:-translate-y-1 active:scale-95 tap-highlight-transparent ${
                    inputType === 'live' 
                      ? '!bg-teal-600 !text-white !border-teal-600 shadow-xl shadow-teal-500/50 ring-2 ring-teal-400' 
                      : 'bg-transparent text-teal-700 dark:text-teal-300 border-2 border-teal-300 hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 shadow-md hover:shadow-lg hover:shadow-teal-300/50'
                  }`}>
                  <Video className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Live
                  <span className={`ml-1 text-[10px] md:text-xs px-1.5 py-0.5 rounded-full ${inputType === 'live' ? 'bg-white/20 text-white' : 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'}`}>5⚡</span>
                </Button>
              </div>

            {inputType === 'document' ? (
              <div className="relative">
                {!isAuthenticated && !isDemoAnalysis && (
                  <div className="absolute inset-0 z-50" onClick={() => setShowAuthOverlay(true)}>
                    {/* Invisible clickable layer */}
                  </div>
                )}
                <DocumentAnalysis 
                  userId={userId} 
                  darkMode={darkMode} 
                  user={user}
                  authToken={authToken}
                />
                {!isAuthenticated && !isDemoAnalysis && showAuthOverlay && (
                  <AuthPromptOverlay 
                    onLoginClick={() => {
                      setShowAuthOverlay(false);
                      setShowAuthModal(true);
                    }}
                    onClose={() => setShowAuthOverlay(false)}
                  />
                )}
              </div>
            ) : inputType === 'compare' ? (
              <div className="relative">
                {!isAuthenticated && !isDemoAnalysis && (
                  <div className="absolute inset-0 z-50" onClick={() => setShowAuthOverlay(true)}>
                    {/* Invisible clickable layer */}
                  </div>
                )}
                <CompareImages 
                  userId={userId} 
                  darkMode={darkMode}
                  onAuthRequired={() => setShowAuthModal(true)}
                  onInsufficientCredits={(credits) => {
                    setRequiredCredits(credits);
                    setShowInsufficientCredits(true);
                  }}
                />
                {!isAuthenticated && !isDemoAnalysis && showAuthOverlay && (
                  <AuthPromptOverlay 
                    onLoginClick={() => {
                      setShowAuthOverlay(false);
                      setShowAuthModal(true);
                    }}
                    onClose={() => setShowAuthOverlay(false)}
                  />
                )}
              </div>
            ) : (
            <>
            <div className="relative">
            <Card className={`analysis-area-gradient ${darkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'} shadow-lg`}>
              <CardContent className="pt-4 md:pt-6">
                {inputType === 'image' && (
                  <div className="text-center">
                    {imagePreview ? (
                      <div>
                        <img src={imagePreview} alt="Preview" className="max-h-48 md:max-h-64 mx-auto rounded mb-3 md:mb-4" />
                        <Button variant="outline" onClick={() => { setImageFile(null); setImagePreview(null); }} className={`text-sm ${darkMode ? 'border-gray-600' : 'border-blue-200'}`}>Remove</Button>
                      </div>
                    ) : (
                      <div 
                        className={`border-2 border-dashed ${darkMode ? 'border-gray-600 bg-gray-700/30' : 'border-blue-200 bg-blue-50/30'} rounded-lg p-8 md:p-12 hover:border-blue-400 transition-colors relative`}
                        onClick={() => {
                          if (!isAuthenticated && !isDemoAnalysis) {
                            setShowAuthOverlay(true);
                          }
                        }}
                      >
                        <Upload className={`w-8 h-8 md:w-12 md:h-12 mx-auto ${darkMode ? 'text-gray-400' : 'text-blue-400'} mb-3 md:mb-4`} />
                        <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} text-xs md:text-sm mb-4 space-y-1`}>
                          <p>Upload product photos, ads, designs, presentations</p>
                          <p className="text-xs">Example: Product packaging → <span className={darkMode ? 'text-blue-400' : 'text-blue-500'}>Joy: 85%, Trust: 70%</span></p>
                          <p className="text-xs">Example: Marketing banner → <span className={darkMode ? 'text-purple-400' : 'text-purple-500'}>Excitement across Gen Z</span></p>
                        </div>
                        {isAuthenticated || isDemoAnalysis ? (
                          <Input 
                            ref={imageInputRef}
                            type="file" 
                            accept="image/*" 
                            onChange={handleImageChange}
                            className="max-w-xs mx-auto text-sm" 
                          />
                        ) : (
                          <Button 
                            variant="outline" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowAuthOverlay(true);
                            }}
                            className="mx-auto transition-all duration-200 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-300/50 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 active:scale-95"
                          >
                            Choose Image
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {inputType === 'video' && (
                  <div className="text-center">
                    {videoPreview ? (
                      <div>
                        {/* Video Player - Click to Enlarge */}
                        <div 
                          className="relative cursor-pointer group"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowVideoModal(true);
                          }}
                        >
                          <video 
                            src={videoPreview} 
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="max-h-64 mx-auto rounded mb-4 transition-transform group-hover:scale-105 pointer-events-none"
                            poster={videoPreview + "#t=0.1"}
                            preload="metadata"
                            onLoadedMetadata={(e) => {
                              const duration = e.target.duration;
                              setVideoDuration(duration);
                              setVideoCredits(15);  // Flat 15 credits for all videos
                              
                              if (duration > 90) {
                                toast.error('Video must be 90 seconds or less for fast processing');
                                setVideoFile(null);
                                setVideoPreview(null);
                                setVideoDuration(0);
                                setVideoCredits(15);
                              }
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          />
                          {/* Click to Enlarge Overlay */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity rounded">
                            <div className="bg-white dark:bg-gray-800 px-3 py-2 rounded-lg text-sm font-medium">
                              Click to play with sound 🔊
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-3">
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Duration: {videoDuration ? videoDuration.toFixed(1) : '0.0'}s {videoDuration > 90 && <span className="text-red-500">(Too long! Max 90s)</span>}
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            File size: {videoFile ? (videoFile.size / (1024 * 1024)).toFixed(1) : '0.0'}MB
                          </p>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                              15⚡ credits
                            </span>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={() => { 
                            setVideoFile(null); 
                            setVideoPreview(null); 
                            setVideoDuration(0);
                          }} 
                          className={`text-sm ${darkMode ? 'border-gray-600' : 'border-red-200'}`}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className={`border-2 border-dashed ${darkMode ? 'border-gray-600 bg-gray-700/30' : 'border-red-200 bg-red-50/30'} rounded-lg p-8 md:p-12 hover:border-red-400 transition-colors relative`}
                        onClick={() => {
                          if (!isAuthenticated && !isDemoAnalysis) {
                            setShowAuthOverlay(true);
                          } else {
                            document.getElementById('video-upload-input')?.click();
                          }
                        }}
                      >
                        <Video className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 text-red-500" />
                        <p className={`text-base md:text-lg font-semibold mb-1 md:mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {isAuthenticated || isDemoAnalysis ? 'Upload Video' : 'Login to Upload Video'}
                        </p>
                        <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                          MP4, MOV • Max 90 seconds • Any file size
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'} font-semibold`}>
                          15⚡ credits • Auto-compressed to 1/10th size for fast analysis
                        </p>
                        {isAuthenticated || isDemoAnalysis ? (
                          <input 
                            ref={videoInputRef}
                            id="video-upload-input"
                            type="file" 
                            accept="video/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Log file details for debugging
                                console.log(`📹 Video file selected:`, {
                                  name: file.name,
                                  type: file.type,
                                  size: `${(file.size / (1024 * 1024)).toFixed(2)}MB`
                                });
                                
                                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
                                console.log(`📹 Video file selected: ${file.name}, Size: ${fileSizeMB}MB`);
                                
                                // No file size limit - we compress everything!
                                // Show compression info toast
                                toast.info(`📹 Video loaded: ${fileSizeMB}MB. We'll compress it for faster processing!`, { duration: 3000 });
                                
                                // IMMEDIATELY show thumbnail from original file
                                const originalUrl = URL.createObjectURL(file);
                                setVideoPreview(originalUrl);
                                setVideoFile(file);  // Set file immediately
                                console.log('✅ Video preview set immediately');
                                
                                // Check if compression is needed
                                const compressionCheck = shouldCompress(file);
                                console.log(`Compression check:`, compressionCheck);
                                
                                if (compressionCheck.shouldCompress) {
                                  // Show compression UI
                                  setVideoCompressing(true);
                                  setCompressionProgress(0);
                                  
                                  const estimatedSize = estimateCompressedSize(fileSizeMB);
                                  toast.info(`📦 Compressing ${fileSizeMB}MB → ~${estimatedSize}MB...`, { duration: 5000 });
                                  
                                  try {
                                    // Compress the video with progress tracking
                                    const compressed = await compressVideo(file, (percent, status) => {
                                      console.log(`Compression: ${percent}% - ${status}`);
                                      setCompressionProgress(percent);
                                    });
                                    
                                    const compressedSizeMB = (compressed.compressedSize / (1024 * 1024)).toFixed(1);
                                    toast.success(`✅ Compressed: ${fileSizeMB}MB → ${compressedSizeMB}MB (${compressed.compressionRatio}% smaller)`, { duration: 3000 });
                                    
                                    // Update with compressed file
                                    setVideoFile(compressed.file);
                                    
                                    // Update preview with compressed video
                                    URL.revokeObjectURL(originalUrl);
                                    const compressedUrl = URL.createObjectURL(compressed.file);
                                    setVideoPreview(compressedUrl);
                                    console.log('✅ Video preview updated with compressed file');
                                    
                                    setVideoCompressing(false);
                                    setCompressionProgress(100);
                                  } catch (error) {
                                    console.error('Compression failed:', error);
                                    toast.error('Compression failed. Using original file...');
                                    setVideoFile(file);
                                    setVideoCompressing(false);
                                    setCompressionProgress(0);
                                  }
                                } else {
                                  // File is small enough, no compression needed
                                  toast.success(`Video loaded: ${fileSizeMB}MB`, { duration: 2000 });
                                  setVideoFile(file);
                                }
                              }
                            }}
                            className="hidden" 
                          />
                        ) : (
                          <Button 
                            variant="outline" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowAuthOverlay(true);
                            }}
                            className="mx-auto mt-3"
                          >
                            Choose Video
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {inputType === 'text' && (
                  <div className="relative">
                    <Textarea 
                      placeholder="Example: 'Revolutionary AI platform transforming businesses' → Anticipation: 78%, Curiosity: 65%&#10;&#10;Enter your text, message, ad copy, or any content..." 
                      rows={6} 
                      value={textInput} 
                      onChange={(e) => {
                        setTextInput(e.target.value);
                        // If user manually types, it's no longer a demo
                        if (isDemoAnalysis && e.target.value !== textInput) {
                          setIsDemoAnalysis(false);
                        }
                      }}
                      disabled={!isAuthenticated && !isDemoAnalysis}
                      className={`w-full resize-none text-sm md:text-base font-medium transition-all duration-300 bg-white border-blue-100 text-gray-900 placeholder:text-gray-400 ${
                        activeTyping === 'content' ? 'ring-4 ring-yellow-400 scale-[1.02]' : ''
                      } ${darkMode ? '!bg-gray-700 !border-gray-600 !text-gray-100 !placeholder:text-gray-500' : ''} focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${(!isAuthenticated && !isDemoAnalysis) ? 'cursor-pointer' : ''}`} 
                    />
                    {/* Invisible overlay to show auth prompt */}
                    {!isAuthenticated && !isDemoAnalysis && (
                      <div 
                        className="absolute inset-0 cursor-pointer z-10"
                        onClick={() => setShowAuthOverlay(true)}
                      />
                    )}
                  </div>
                )}
                {inputType === 'url' && (
                  <div className="relative">
                    <Input 
                      type="url" 
                      placeholder="Example: https://apple.com/iphone → Trust: 82%, Desire: 75%" 
                      value={urlInput} 
                      onChange={(e) => setUrlInput(e.target.value)}
                      disabled={!isAuthenticated && !isDemoAnalysis}
                      className={`text-sm md:text-base font-medium ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder:text-gray-500' : 'bg-white border-blue-100 text-gray-900 placeholder:text-gray-400'} focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${(!isAuthenticated && !isDemoAnalysis) ? 'cursor-pointer' : ''}`} 
                    />
                    {/* Invisible overlay to show auth prompt */}
                    {!isAuthenticated && !isDemoAnalysis && (
                      <div 
                        className="absolute inset-0 cursor-pointer z-10"
                        onClick={() => setShowAuthOverlay(true)}
                      />
                    )}
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'} mt-2 italic`}>We analyze the actual webpage content</p>
                  </div>
                )}
                {inputType === 'live' && (
                  <div className="relative">
                    <LiveCamera 
                      key={liveCameraKey}
                      darkMode={darkMode} 
                      userId={userId} 
                      audienceFilters={getAudienceFilters()}
                      user={user}
                      authToken={authToken}
                    />
                    {!isAuthenticated && !isDemoAnalysis && (
                      <div className="absolute inset-0 z-50">
                        <div onClick={() => setShowAuthOverlay(true)} className="absolute inset-0 cursor-pointer" />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Compare Upload Interface */}
                {inputType === 'best-of-3' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {bestOfThreeImages.map((img, index) => (
                        <div key={img.id} className="relative group">
                          <img
                            src={img.preview}
                            alt={`Image ${index + 1}`}
                            className={`w-full h-48 object-cover rounded-lg border-2 ${darkMode ? 'border-purple-500' : 'border-purple-400'}`}
                          />
                          <button
                            onClick={() => setBestOfThreeImages(prev => prev.filter(i => i.id !== img.id))}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-bold ${darkMode ? 'bg-purple-600' : 'bg-purple-500'} text-white`}>
                            Image #{index + 1}
                          </div>
                        </div>
                      ))}
                      
                      {bestOfThreeImages.length < 3 && (
                        <label className={`flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                          darkMode 
                            ? 'border-gray-600 hover:border-purple-500 bg-gray-800' 
                            : 'border-gray-300 hover:border-purple-500 bg-gray-50'
                        }`}>
                          <Upload className="w-8 h-8 mb-2 text-purple-500" />
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {bestOfThreeImages.length === 0 ? 'Upload images (2-3)' : `Add image ${bestOfThreeImages.length + 1}`}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">Click to select</span>
                          <input
                            ref={bestOfThreeInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files);
                              if (bestOfThreeImages.length + files.length > 3) {
                                toast.error('Maximum 3 images allowed');
                                return;
                              }
                              const newImages = files.map(file => ({
                                file,
                                preview: URL.createObjectURL(file),
                                id: Math.random().toString(36).substr(2, 9)
                              }));
                              setBestOfThreeImages(prev => [...prev, ...newImages]);
                            }}
                            className="hidden"
                            disabled={!isAuthenticated && !isDemoAnalysis}
                          />
                        </label>
                      )}
                    </div>
                    
                    {bestOfThreeImages.length > 0 && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-purple-900/20 border border-purple-700' : 'bg-purple-50 border border-purple-200'}`}>
                        <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                          <Trophy className="inline w-4 h-4 mr-1" />
                          <strong>{bestOfThreeImages.length}</strong> image{bestOfThreeImages.length > 1 ? 's' : ''} uploaded. 
                          {bestOfThreeImages.length < 2 && ' Add at least 1 more image to compare.'}
                        </p>
                      </div>
                    )}
                    
                    {!isAuthenticated && !isDemoAnalysis && (
                      <div onClick={() => setShowAuthOverlay(true)} className="absolute inset-0 cursor-pointer z-10" />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Auth Overlay - Show when non-authenticated user tries to input */}
            {!isAuthenticated && !isDemoAnalysis && showAuthOverlay && (
              <AuthPromptOverlay 
                onLoginClick={() => {
                  setShowAuthOverlay(false);
                  setShowAuthModal(true);
                }}
                onClose={() => setShowAuthOverlay(false)}
              />
            )}
            </div>

            {/* Custom Question Field - Moved above Target Audience */}
            <Card className={`analysis-area-gradient ${darkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'} shadow-lg`}>
              <CardContent className="pt-4 md:pt-6">
                <Label className={`text-sm md:text-base font-semibold mb-3 block ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Lightbulb className="inline w-4 h-4 md:w-5 md:h-5 mr-2 text-yellow-600" />
                  Add details for better analysis
                </Label>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                  Ask EmoQ to analyze something specific about your content
                </p>
                <div className="relative">
                  <textarea
                    value={customQuestion}
                    onChange={(e) => setCustomQuestion(e.target.value)}
                    disabled={!isAuthenticated && !isDemoAnalysis}
                    placeholder="E.g., 'How would this perform as a social media ad?' or 'What's the best time to post this?'"
                    className={`w-full p-3 rounded-lg border text-sm md:text-base font-medium transition-all duration-300 bg-white border-blue-100 text-gray-900 placeholder:text-gray-400 ${
                      activeTyping === 'question' ? 'ring-4 ring-yellow-400 scale-[1.02]' : ''
                    } ${darkMode ? '!bg-gray-700 !border-gray-600 !text-gray-100 !placeholder:text-gray-500' : ''} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${(!isAuthenticated && !isDemoAnalysis) ? 'cursor-pointer' : ''}`}
                    rows="3"
                  />
                  {/* Invisible overlay to show auth prompt */}
                  {!isAuthenticated && !isDemoAnalysis && (
                    <div 
                      className="absolute inset-0 cursor-pointer z-10"
                      onClick={() => setShowAuthOverlay(true)}
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Target Audience */}
            <Card className={`analysis-area-gradient ${darkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'} shadow-lg`}>
              <CardContent className="pt-4 md:pt-6 space-y-3 md:space-y-4">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setAudienceFiltersExpanded(!audienceFiltersExpanded)}
                >
                  <Label className={`text-sm md:text-base font-semibold flex items-center gap-2 cursor-pointer ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                    Target Audience
                  </Label>
                  <button 
                    type="button"
                    className={`text-gray-500 hover:text-gray-700 ${darkMode ? 'hover:text-gray-300' : ''}`}
                  >
                    {audienceFiltersExpanded ? '▲' : '▼'}
                  </button>
                </div>

                {audienceFiltersExpanded && (
                  <>
                    <div>
                      <Label className={`text-xs md:text-sm mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</Label>
                      <Select value={region} onValueChange={setRegion}>
                        <SelectTrigger className={`text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-blue-100'}`}>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
                          {audienceOptions?.locations?.map((location, idx) => (
                            <SelectItem key={location} value={location} className={`text-sm ${darkMode ? 'text-white' : ''}`}>
                              {location}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className={`text-xs md:text-sm mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Gender</Label>
                      <Select value={gender} onValueChange={setGender}>
                        <SelectTrigger className={`text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-blue-100'}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
                          {audienceOptions?.genders.map(g => <SelectItem key={g} value={g} className={`text-sm ${darkMode ? 'text-white' : ''}`}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className={`text-xs md:text-sm mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Age Range: {ageRange[0]} - {ageRange[1]}</Label>
                      <Slider value={ageRange} onValueChange={setAgeRange} min={13} max={85} step={1} className="mt-2" />
                    </div>

                    <div>
                      <Label className={`text-xs md:text-sm mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Professions (Optional)</Label>
                      <div className="flex flex-wrap gap-2">
                        {audienceOptions?.professions.map(prof => (
                          <Badge key={prof} variant={selectedProfessions.includes(prof) ? 'default' : 'outline'} className={`cursor-pointer text-xs ${selectedProfessions.includes(prof) ? 'bg-blue-600' : darkMode ? 'border-gray-600 text-gray-300' : 'border-blue-200'}`}
                            onClick={() => {
                              if (selectedProfessions.includes(prof)) {
                                setSelectedProfessions(selectedProfessions.filter(p => p !== prof));
                              } else {
                                setSelectedProfessions([...selectedProfessions, prof]);
                              }
                            }}
                          >
                            {prof}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Helpful prompt when analysis area is highlighted */}
            {highlightAnalysis && (
              <div className={`text-center py-2 animate-bounce ${darkMode ? 'text-blue-400' : 'text-blue-600'} font-semibold flex items-center justify-center gap-2`}>
                <span>👇</span>
                <span>Click the button below to start your analysis!</span>
                <span>👇</span>
              </div>
            )}
            
            {/* Credit Cost Indicator */}
            {!isDemoAnalysis && isAuthenticated && (
              <div className={`mb-3 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="inline-flex items-center gap-1">
                  This analysis costs 
                  <span className={`font-bold px-2 py-1 rounded-md ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                    {inputType === 'text' ? '2' : 
                     inputType === 'compare' ? '10' : 
                     inputType === 'document' ? '20' : 
                     inputType === 'video' ? '15' :
                     inputType === 'best-of-3' ? '15' : '5'} credits ⚡
                  </span>
                </span>
              </div>
            )}

            {/* Video Compression Progress Bar - Above Analyze Button */}
            {videoCompressing && compressionProgress < 100 && (
              <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">📦 Compressing video...</span>
                  <span className="text-sm font-bold text-blue-700 dark:text-blue-300">{compressionProgress}%</span>
                </div>
                <Progress value={compressionProgress} className="h-2" />
              </div>
            )}
            
            {/* Compression Complete Message - Above Analyze Button */}
            {!videoCompressing && compressionProgress === 100 && videoPreview && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 animate-pulse">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm font-bold text-green-700 dark:text-green-300">✅ Compression complete! Ready to analyze</span>
                </div>
              </div>
            )}

            <Button 
              onClick={() => {
                setPulseButton(false); // Stop pulsing when clicked
                handleAnalyze();
              }} 
              disabled={loading} 
              className={`w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 py-5 md:py-6 text-base md:text-lg shadow-lg hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] text-white ${
                highlightAnalysis ? 'animate-pulse ring-4 ring-blue-400' : ''
              } ${
                pulseButton ? 'animate-pulse ring-4 ring-yellow-400 scale-105' : ''
              } ${
                !videoCompressing && compressionProgress === 100 && videoPreview ? 'animate-pulse ring-4 ring-green-400 scale-105' : ''
              } ${!loading ? 'hover:-translate-y-0.5' : ''}`}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 md:w-5 md:h-5 animate-spin text-white" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 w-4 h-4 md:w-5 md:h-5 text-white" />
                  {isDemoAnalysis ? 'Analyze Demo Content' : isAuthenticated ? 'Analyze My Content' : 'Analyze My Content'}
                </>
              )}
            </Button>
            
            {/* Progress Bar with Fun Facts */}
            {loading && (
              <>
                <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'} shadow-lg`}>
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className={darkMode ? 'text-white' : 'text-gray-700'}>Analysis in progress...</span>
                        <span className={`font-bold ${darkMode ? 'text-white' : 'text-blue-600'}`}>{Math.min(Math.round(analysisProgress), 100)}%</span>
                      </div>
                      <Progress value={Math.min(analysisProgress, 100)} className="h-2" />
                    </div>
                    {currentFunFact && (
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700' : 'bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-300'} animate-pulse`}>
                        <p className={`text-sm text-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          💡 {currentFunFact}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Show notification prompt for ALL analysis types - only if loading and not already subscribed */}
                {/* TEMPORARILY DISABLED - VAPID keys need configuration
                {loading && isAuthenticated && user && showNotificationPrompt && (
                  <AnalysisNotificationPrompt
                    user={user}
                    apiUrl={API}
                    darkMode={darkMode}
                    onDismiss={() => setShowNotificationPrompt(false)}
                  />
                )}
                */}
              </>
            )}
            </>
            )}
            </div>
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6">
            {/* Scroll Prompt */}
            {showScrollPrompt && (
              <div className={`fixed bottom-8 right-8 z-50 animate-bounce ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-2`}>
                <span className="text-sm font-semibold">Scroll down to see full results</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            )}
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-6">
              <h2 className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>Analysis Results</h2>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={handleDownloadPDF}
                  variant="default"
                  className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Download PDF
                </Button>
                <Button 
                  onClick={handleDownloadReport}
                  variant="default"
                  className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Word
                </Button>
                <ShareButton analysisId={result.id} darkMode={darkMode} />
                <Button onClick={goHome} variant="outline" className={`text-sm md:text-base ${darkMode ? 'border-gray-600 text-gray-300' : 'border-blue-200'}`}>New Analysis</Button>
              </div>
            </div>

            {result.content_description && (
              <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
                <CardContent className="pt-6">
                  <Label className={`text-sm font-semibold mb-3 block ${darkMode ? 'text-blue-400' : 'text-blue-900'}`}>Content Analysis</Label>
                  <FormattedContentAnalysis content={result.content_description} darkMode={darkMode} />
                </CardContent>
              </Card>
            )}

            {/* Show image thumbnail for non-video analyses only (videos show thumbnail in VideoAnalysisResults) */}
            {result.image_data && result.content_type !== 'video' && (
              <Card className={darkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'}>
                <CardContent className="pt-6">
                  <Label className={`text-sm mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Analyzed Content</Label>
                  <img 
                    src={result.image_data.startsWith('data:') ? result.image_data : `data:image/jpeg;base64,${result.image_data}`}
                    alt="Analyzed" 
                    className="w-full max-h-64 md:max-h-96 object-contain rounded" 
                  />
                </CardContent>
              </Card>
            )}

            {/* Check if this is Compare results */}
            {result.is_best_of_three && result.all_results ? (
              /* Compare Results Display */
              <BestOfThreeResults
                results={result.all_results}
                darkMode={darkMode}
                user={user}
                authToken={authToken}
              />
            ) : (
              /* Standard Analysis Results */
              <div id="analysis-results-pdf">
                <ComprehensiveAnalysisResults result={result} darkMode={darkMode} />
                
                {/* AI Enhancement Section - Inside PDF container */}
                <div className="mt-8">
                  <AIEnhancement 
                    analysis={result} 
                    darkMode={darkMode} 
                    onCreditsUpdate={updateCredits}
                    isDemoAnalysis={isDemoAnalysis}
                    demoPersonaKey={demoPersonaKey}
                    demoEnhancement={isDemoAnalysis && demoPersonaKey && demoPersonaData[demoPersonaKey]?.enhancement}
                    user={user}
                    authToken={authToken}
                  />
                </div>
              </div>
            )}

            {/* Rating & Feedback Section - Outside PDF */}
            <div className="mt-8">
              <RatingFeedback analysisId={result.id} darkMode={darkMode} />
            </div>
          </div>
        )}
      </div>

      {/* Stats Section - The Global Content Explosion - Lazy Load */}
      {!result && !loading && (
        <div className={`py-12 md:py-16 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-b from-blue-50 to-white'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-8 md:mb-12">
              <h2 className={`text-2xl md:text-3xl font-bold mb-3 md:mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>
                The Global Content Explosion
              </h2>
              <p className={`text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'} max-w-3xl mx-auto leading-relaxed`}>
                Every minute, billions of pieces of content fight for attention. EmoQ.ai helps your content win hearts and outcomes—before you hit "publish."
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { stat: '6h 38m', label: 'Average daily online time', source: 'Global Web Index 2025' },
                { stat: '177,000', label: 'New websites daily', source: 'Siteefy 2025' },
                { stat: '376B', label: 'Emails sent daily', source: 'Statista 2025' },
                { stat: '73%', label: 'Video as #1 content format', source: 'Wyzowl 2025' },
                { stat: '$376.6B', label: 'Creator economy by 2030', source: 'Goldman Sachs 2025' },
                { stat: '54 sec', label: 'Average time per webpage', source: 'Industry Research' }
              ].map((item, idx) => (
                <div key={idx} className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-blue-100'} shadow-lg text-center`}>
                  <div className={`text-3xl md:text-4xl font-bold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} style={{fontFamily: 'Space Grotesk'}}>
                    {item.stat}
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {item.label}
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {item.source}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Problem Statement - Lazy Load */}
      {!result && !loading && (
        <div className={`py-12 md:py-16 ${darkMode ? 'bg-gray-800' : 'bg-gradient-to-b from-white to-blue-50'}`}>
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>
              The Problem
            </h2>
            <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'} max-w-3xl mx-auto leading-relaxed`}>
              Content is everywhere—but emotional connection is rare. Brands, creators, and teams spend millions on content without knowing how it makes people feel.
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                <div className={`text-3xl font-bold mb-2 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>54 sec</div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Average time per webpage</p>
              </div>
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                <div className={`text-3xl font-bold mb-2 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>70%</div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Users scroll past content that doesn't emotionally engage</p>
              </div>
            </div>
            <p className={`text-lg md:text-xl font-bold mt-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              If your content doesn't connect emotionally—it disappears.
            </p>
          </div>
        </div>
      )}

      {/* Why Emotion Matters - Lazy Load */}
      {!result && !loading && (
        <div className={`py-12 md:py-16 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-8 md:mb-12">
              <h2 className={`text-2xl md:text-3xl font-bold mb-3 md:mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>
                Why Emotion Matters
              </h2>
              <p className={`text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'} max-w-3xl mx-auto leading-relaxed`}>
                Emotion is the single biggest driver of engagement, loyalty, and sales.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'} text-center`}>
                <div className={`text-4xl font-bold mb-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>+23%</div>
                <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Higher Sales</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Emotionally resonant ads</p>
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Nielsen Consumer Neuroscience</p>
              </div>
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'} text-center`}>
                <div className={`text-4xl font-bold mb-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>2x</div>
                <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Customer Value</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Emotionally connected customers</p>
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Harvard Business Review</p>
              </div>
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'} text-center`}>
                <div className={`text-4xl font-bold mb-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Longer</div>
                <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Brand Growth</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Emotion-led campaigns</p>
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>IPA Effectiveness Studies</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Use Cases - REMOVED (duplicate content) */}
      {false && !result && (
        <div className={`py-12 md:py-16 ${darkMode ? 'bg-gray-800' : 'bg-gradient-to-b from-blue-50 to-white'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-8 md:mb-12">
              <h2 className={`text-2xl md:text-3xl font-bold mb-3 md:mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>
                Who Uses EmoQ.ai?
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { audience: 'Marketers & Brands', goal: 'Higher engagement & CTR', result: '+20–30% emotional lift' },
                { audience: 'Creators & Influencers', goal: 'More viral content', result: 'Audience resonance prediction' },
                { audience: 'UX & Product Teams', goal: 'Emotional design validation', result: 'Increased user trust & retention' },
                { audience: 'Enterprises', goal: 'Leadership communication', result: 'Improved connection & clarity' },
                { audience: 'Agencies', goal: 'Pre-test campaigns', result: 'Optimized for sentiment & recall' },
                { audience: 'Educators & Nonprofits', goal: 'Compelling storytelling', result: 'Deeper audience engagement' }
              ].map((item, idx) => (
                <div key={idx} className={`p-6 rounded-xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-blue-100'} shadow-lg hover:shadow-xl transition-shadow`}>
                  <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {item.audience}
                  </h3>
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    🎯 {item.goal}
                  </p>
                  <p className={`text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    🚀 {item.result}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Features Section - REMOVED (duplicate content) */}
      {false && !result && (
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-100'} border-t py-12 md:py-16`}>
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-8 md:mb-12">
              <h2 className={`text-2xl md:text-3xl font-bold mb-3 md:mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>Why EmoQ.ai?</h2>
              <p className={`text-sm md:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>The world's first Emotional Intelligence Engine for Content</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center">
                <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${darkMode ? 'bg-blue-900' : 'bg-blue-100'} flex items-center justify-center mx-auto mb-3 md:mb-4`}>
                  <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                </div>
                <h3 className={`text-sm md:text-base font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Deep Analytics</h3>
                <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>14 emotion categories across 8 demographic segments</p>
              </div>
              <div className="text-center">
                <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${darkMode ? 'bg-indigo-900' : 'bg-indigo-100'} flex items-center justify-center mx-auto mb-3 md:mb-4`}>
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-indigo-600" />
                </div>
                <h3 className={`text-sm md:text-base font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Actionable Insights</h3>
                <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>Specific recommendations to optimize emotional resonance</p>
              </div>
              <div className="text-center">
                <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${darkMode ? 'bg-purple-900' : 'bg-purple-100'} flex items-center justify-center mx-auto mb-3 md:mb-4`}>
                  <Brain className="w-6 h-6 md:w-8 md:h-8 text-purple-600" />
                </div>
                <h3 className={`text-sm md:text-base font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>AI-Powered</h3>
                <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>Multi-provider LLM for accurate emotional quotient analysis</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Auth Modal */}
      <AuthModal 
        open={showAuthModal} 
        onOpenChange={setShowAuthModal}
        onSuccess={() => {
          setShowAuthModal(false);
          // Refresh user data
          if (updateCredits) {
            updateCredits();
          }
        }}
      />
      
      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        open={showInsufficientCredits}
        onOpenChange={setShowInsufficientCredits}
        currentCredits={user?.credits || 0}
        requiredCredits={requiredCredits}
        actionName="this analysis"
      />
      
      {/* Video Enlargement Modal with Custom Controls */}
      {showVideoModal && videoPreview && (
        <div 
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-20 bg-black/70 hover:bg-black/90 text-white rounded-full w-10 h-10 p-0"
              onClick={() => setShowVideoModal(false)}
            >
              <X className="w-6 h-6" />
            </Button>
            
            <video
              ref={(el) => {
                if (el) {
                  el.volume = 1.0; // Full volume
                }
              }}
              src={videoPreview}
              autoPlay
              playsInline
              loop
              onClick={(e) => {
                const video = e.target;
                if (video.paused) {
                  video.play();
                } else {
                  video.pause();
                }
              }}
              className="modal-video-player w-full h-full object-contain cursor-pointer"
            />
            
            {/* Custom Video Controls Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-center gap-4">
                {/* -10s Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    const video = document.querySelector('.modal-video-player');
                    if (video) video.currentTime = Math.max(0, video.currentTime - 10);
                  }}
                >
                  <div className="text-xs font-bold">-10s</div>
                </Button>
                
                {/* Unmute/Sound Button - Prominent! */}
                <Button
                  variant="ghost"
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-16 h-16 p-0 flex flex-col items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    const video = document.querySelector('.modal-video-player');
                    if (video) {
                      video.muted = !video.muted;
                      // Force update to show new state
                      const icon = e.currentTarget.querySelector('.volume-icon');
                      if (icon) {
                        icon.textContent = video.muted ? '🔇' : '🔊';
                      }
                    }
                  }}
                >
                  <span className="volume-icon text-2xl">🔊</span>
                  <span className="text-xs mt-1">Sound ON</span>
                </Button>
                
                {/* +10s Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    const video = document.querySelector('.modal-video-player');
                    if (video) video.currentTime = Math.min(video.duration, video.currentTime + 10);
                  }}
                >
                  <div className="text-xs font-bold">+10s</div>
                </Button>
              </div>
            </div>
            
            <style jsx>{`
              .modal-video-player {
                object-fit: contain;
              }
            `}</style>
          </div>
        </div>
      )}

      {/* Persona Demo Modal */}
      <PersonaDemoModal
        isOpen={showDemoModal}
        onClose={() => {
          setShowDemoModal(false);
          setSelectedDemoPersona(null);
        }}
        demoData={selectedDemoPersona?.data}
        personaRole={selectedDemoPersona?.role}
        personaIcon={selectedDemoPersona?.icon}
        darkMode={darkMode}
      />

      {/* Footer */}
      <Footer darkMode={darkMode} />
      
      {/* Scroll to Top Button */}
      <ScrollToTop darkMode={darkMode} />
    </div>
  );
};

export default Home;